import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { HookInputProps, outputType } from 'types/hooks';
import { errorMessage } from 'config/constants';
import { getLenseConfigParser } from 'parser/pricingSimulator/lenseConfigParser';
import { DimensionConfigType, LenseConfig, Scenario } from 'types/pricingSimulator';
import _ from 'lodash';
import useUpdateLastViewedLense from './lens/useUpdateLastViewedLense';

interface FetchLenseConfigProp extends HookInputProps {
    setDimensionConfigData: Dispatch<SetStateAction<DimensionConfigType | null>>;
}

function useFetchDimensionConfigData({
    url,
    method,
    onComplete,
    onError,
    queryKey,
    variables = {},
    queryConfig = {},
    setDimensionConfigData,
}: FetchLenseConfigProp) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);
    const [count, setCount] = useState(0);

    const { mutate: triggerLastViewed } = useUpdateLastViewedLense();

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //only set data to null if function is being called outside/without infinite scroll
            //if offset is equal to default offset, it means request is coming without infinite scroll
            if (variables?.offset === 0) {
                setData(null);
                setError(null);
            }
            setLoading(true);

            try {
                const data = await axios({
                    method,
                    url,
                    params: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;
    //error handling
    if (!apiLoading && apiError && !error) {
        if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            if (payload && Array.isArray(payload)) {
                // Filter and map for dim1 with is_dim true
                const dim1DropdownList = payload
                    .filter((item) => item.dimension === 'dim1' && item.is_dim)
                    .map((item) => ({ label: item.label, value: item.value, dimension: item.dimension }));

                const defaultDim1DropdownItem = dim1DropdownList[dim1DropdownList?.length - 1];

                // Filter and map for dim2 with is_dim true
                const dim2DropdownList = payload
                    .filter((item) => item.dimension === 'dim2' && item.is_dim)
                    .map((item) => ({ label: item.label, value: item.value, dimension: item.dimension }));

                const defaultDim2DropdownItem = dim2DropdownList[dim2DropdownList?.length - 1];

                // Filter and map for scenarioFilterKeys with is_filter true
                const scenarioFilterKeys = payload
                    .filter((item) => item.is_filter)
                    .map((item) => ({ label: item.label, value: item.value, dimension: item.dimension }));
                //DIM1 Attribute type info
                const dim1Attribute = payload.find((item) => {
                    return item.is_project_dim && !item?.nested_id && item.dimension === 'dim1';
                });
                //DIM1 nested Attribute type info

                const dim1NestedAttribute = payload.find((item) => {
                    return item.is_project_dim && item.nested_id && item.dimension === 'dim1';
                });
                //DIM2 Attribute type info

                const dim2Attribute = payload.find((item) => {
                    return item.is_project_dim && item.dimension === 'dim2';
                });
                //lowest dim names for lenses
                const lowestDimLensNames = payload.filter((item) => item.is_lowest).map((item) => item.value);
                //for multi selected dropdown header
                const scenarioFilterDimensionInfo = payload
                    .filter((item) => item.is_lowest && (item.dimension === 'dim1' || item.dimension === 'dim2'))
                    .reduce((acc, item) => {
                        acc[item.dimension] = { label: item.label, key: item.value };
                        return acc;
                    }, {});

                //setting all required states
                setDimensionConfigData({
                    defaultDim1DropdownItem,
                    defaultDim2DropdownItem,
                    dim1DropdownList,
                    dim2DropdownList,
                    scenarioFilterKeys,
                    dim1Attribute: dim1Attribute
                        ? {
                              dimension: dim1Attribute.dimension,
                              key: dim1Attribute.value,
                              label: dim1Attribute.label,
                              orderByName: dim1Attribute.order_by_name,
                          }
                        : null,
                    dim2Attribute: dim2Attribute
                        ? {
                              dimension: dim2Attribute.dimension,
                              key: dim2Attribute.value,
                              label: dim2Attribute.label,
                              orderByName: dim2Attribute.order_by_name,
                          }
                        : null,
                    dim1NestedAttribute: dim1NestedAttribute
                        ? {
                              dimension: dim1NestedAttribute.dimension,
                              key: dim1NestedAttribute.value,
                              label: dim1NestedAttribute.label,
                              orderByName: dim1NestedAttribute.order_by_name,
                          }
                        : null,
                    lowestDimLensNames,
                    scenarioFilterDimensionInfo,
                });
            }
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }
    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setCount(0);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
        count,
    };
    return outputData;
}

export { useFetchDimensionConfigData };
